import styled from 'styled-components'

import { colors } from 'app/theme/colors'
import { radius, shadow, space, zIndex } from 'app/theme/tokens'

/**
 * Shared layout primitives for list/detail pages. Replaces the per-page
 * Header / FilterBar / Content / ActionBar styled.div blocks that were
 * duplicated across ~20 pages (Attendance, Batch, User, Class, …).
 *
 * Pattern:
 *
 *   <PageLayout>
 *     <PageHeader>
 *       <PageTitle>Memberships</PageTitle>
 *       <PageHeaderActions>{...}</PageHeaderActions>
 *     </PageHeader>
 *     <PageFilterBar>{...search + dropdowns...}</PageFilterBar>
 *     <PageContent>{...table or form...}</PageContent>
 *     <PageActionBar>{...primary CTAs...}</PageActionBar>
 *   </PageLayout>
 *
 * Tokens (from app/theme/tokens.js) drive spacing/shadows so every page
 * picks up future design-system tweaks for free.
 */

export const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  width: 100%;
  background: transparent;
`

export const PageHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.md};
  padding: ${space.md} ${space.lg};
  background: ${colors.white};
  position: sticky;
  top: 0;
  z-index: ${zIndex.sticky};
`

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${colors.textPrimary};
`

export const PageHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${space.sm};
`

export const PageFilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.md} ${space.lg};
  background: ${colors.ohOffWhite};
  border-bottom: 1px solid ${colors.borderDefault};
  flex-wrap: wrap;
`

export const PageContent = styled.main`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: ${space.md};
  padding: ${space.md} ${space.lg};
  overflow: auto;
`

export const PageSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
  padding: ${space.lg};
  background: ${colors.white};
  border-radius: ${radius.md};
  box-shadow: ${shadow.default};
`

export const PageActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${space.sm};
  padding: ${space.md} ${space.lg};
  background: ${colors.white};
  border-top: 1px solid ${colors.borderDefault};
  position: sticky;
  bottom: 0;
  z-index: ${zIndex.sticky};
`

/**
 * Tab container for pages that switch between sub-views (Memberships, Orders,
 * Subjects, Users, Sessions, Enrolments). Replaces ad-hoc `<Paper square>`
 * wrappers and gives every tabbed page identical visual treatment.
 */
export const PageTabsBar = styled.div`
  background: ${colors.white};
  border-bottom: 1px solid ${colors.borderDefault};
  position: sticky;
  top: 0;
  z-index: ${zIndex.sticky};
`
